import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { TeamOwnerSectionData } from '@/types/unifiedGameForm';
import { GameWithDetails } from '@/types/gameSystem';
import { TeamRosterManagement } from './TeamRosterManagement';
import { TeamReadinessConfirmation } from './TeamReadinessConfirmation';
import { Users, Loader2, Save } from 'lucide-react';

interface TeamOwnerSectionProps {
  gameData: GameWithDetails;
  canEdit: boolean;
  canApprove: boolean;
  onSave: (data: TeamOwnerSectionData) => void;
  saving: boolean;
  userTeamId?: number;
  userTeamSide?: 'home' | 'away';
}

export const TeamOwnerSection: React.FC<TeamOwnerSectionProps> = ({
  gameData,
  canEdit,
  canApprove,
  onSave,
  saving,
  userTeamId,
  userTeamSide
}) => {
  const [formData, setFormData] = useState<TeamOwnerSectionData>({
    coaches: {
      assignedCoaches: [],
      coachRoles: {}
    },
    roster: {
      teamPlayers: [],
      playerRoles: {}
    },
    strategy: {
      teamNotes: '',
      gameStrategy: ''
    }
  });

  const handleInputChange = (section: keyof TeamOwnerSectionData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Team Management
          {!canEdit && <Badge variant="outline">View Only</Badge>}
        </CardTitle>
        <CardDescription>
          Manage coaches, team roster, and game strategy
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Team Info */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Team Information</h4>
          <div className="text-sm text-muted-foreground">
            Manage your team's participation in this game
          </div>
        </div>

        {/* Team Roster Management */}
        {userTeamId && userTeamSide && (
          <TeamRosterManagement
            gameId={gameData.id}
            teamId={userTeamId}
            teamSide={userTeamSide}
            teamName={userTeamSide === 'home' ? 'Home Team' : 'Away Team'}
            canEdit={canEdit}
            onPlayersChange={(players) => {
              handleInputChange('roster', 'teamPlayers', players.map(p => p.id));
            }}
          />
        )}

        {/* Team Readiness */}
        {userTeamId && userTeamSide && (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">Team Readiness</h4>
            <TeamReadinessConfirmation
              gameId={gameData.id}
              teamId={userTeamId}
              teamSide={userTeamSide}
              selectedPlayers={formData.roster.teamPlayers}
              startingLineup={formData.roster.teamPlayers.slice(0, 5)}
              isReady={(gameData as any)[`${userTeamSide}_team_ready`] || false}
              readinessDeadline={(gameData as any).readiness_deadline}
              canConfirmReadiness={canEdit}
              onReadinessChange={(isReady) => {
                // Handle readiness change if needed for form state
                console.log('Team readiness changed:', isReady);
              }}
            />
          </div>
        )}

        {/* Strategy & Notes */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold">Team Strategy</h4>
          <div>
            <Label htmlFor="teamNotes">Team Notes</Label>
            <Textarea
              id="teamNotes"
              value={formData.strategy.teamNotes || ''}
              onChange={(e) => handleInputChange('strategy', 'teamNotes', e.target.value)}
              disabled={!canEdit}
              placeholder="General team notes and reminders"
            />
          </div>
          <div>
            <Label htmlFor="gameStrategy">Game Strategy</Label>
            <Textarea
              id="gameStrategy"
              value={formData.strategy.gameStrategy || ''}
              onChange={(e) => handleInputChange('strategy', 'gameStrategy', e.target.value)}
              disabled={!canEdit}
              placeholder="Specific strategy for this game"
            />
          </div>
        </div>

        {/* Actions */}
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Team Information
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};